import React, { useEffect, useState } from "react";
import * as firebase from "firebase/app";
import Crunker from "../Crunker";

import useRecorder from "../hooks/useRecorder";
import useFirebaseStorage from "../hooks/useFirebaseStorage";

const RecordButton = ({ filePath, onUrlChange }) => {
  const { blob, start, stop, status } = useRecorder();
  const { url, put, loading, error } = useFirebaseStorage(filePath);

  useEffect(() => {
    put(blob);
  }, [blob]);

  useEffect(() => {
    onUrlChange(url);
  }, [url]);

  return (
    <React.Fragment>
      {status !== "recording" && !loading && (
        <button
          className={`${
            url
              ? "bg-green-200 hover:bg-green-300 text-green-600 hover:text-green-800"
              : "bg-green-500 hover:bg-green-700 text-white"
          } w-full font-bold py-2 px-4 rounded`}
          onClick={start}
        >
          ⏺ {url ? "Re-" : ""}Record
        </button>
      )}

      {status === "recording" && !loading && (
        <button
          className="w-full bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
          onClick={stop}
        >
          ⏹ Stop
        </button>
      )}

      {loading && !url && (
        <button
          className="w-full bg-blue-500 text-white font-bold py-2 px-4 rounded opacity-50 cursor-not-allowed"
          disabled
        >
          ☁️ Uploading...
        </button>
      )}

      {/* local immediate playback */}
      {false && blob && blob.size > 0 && (
        <audio
          src={window.URL.createObjectURL(blob)}
          className="w-full"
          controls
        />
      )}

      {status !== "recording" && url && !loading && (
        <audio src={url} className="w-full" controls />
      )}
    </React.Fragment>
  );
};

const compose = async ({ openingUrl, closingUrl, filename }) => {
  const introSoundSrc =
    "https://firebasestorage.googleapis.com/v0/b/genco-38c28.appspot.com/o/your-thoughts%2Fintro_sound.wav?alt=media&token=16804c1d-4bfe-4d46-90f9-144488a4acf6";
  // "https://firebasestorage.googleapis.com/v0/b/genco-38c28.appspot.com/o/your-thoughts%2Fintro_sound.mp3?alt=media&token=d3ae3251-f8ba-4d06-bbf6-ce47e2ea1c64";
  const outroSoundSrc =
    "https://firebasestorage.googleapis.com/v0/b/genco-38c28.appspot.com/o/your-thoughts%2Foutro_sound_trimmed.wav?alt=media&token=780fee26-8059-4c70-b353-9241d33042cf";
  // "https://firebasestorage.googleapis.com/v0/b/genco-38c28.appspot.com/o/your-thoughts%2Foutro_sound.mp3?alt=media&token=4458a4ff-d552-4099-a66c-3fce31789167";
  const musicSrc =
    "https://firebasestorage.googleapis.com/v0/b/genco-38c28.appspot.com/o/your-thoughts%2Fmusic.mp3?alt=media&token=ecf97d2d-4bb8-4a1e-b569-8c9ad749e6f9";

  const audio = new Crunker({ sampleRate: 48000 });
  // const audio = new Crunker({ sampleRate: 44100 });

  // merge the opening
  const openingBuffers = await audio.fetchAudio(openingUrl, introSoundSrc);
  const openingBuffer = await audio.mergeAudio(openingBuffers);

  // tack on the closing and outro sound
  const closingBuffers = await audio.fetchAudio(
    musicSrc,
    closingUrl,
    outroSoundSrc
  );
  const episodeBuffer = await audio.concatAudio([
    openingBuffer,
    ...closingBuffers,
  ]);
  const output = await audio.export(episodeBuffer, "audio/mp3");

  audio.download(output.blob, filename);
  // window.output = output;
  // console.log({ output });
  return output.url;

  // try{
  // }catch(error => {
  //   console.log(error);
  //   // => Error Message
  // });
};

const ComposeButton = ({ openingUrl, closingUrl, filename }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  return (
    <React.Fragment>
      <button
        className="w-full bg-pink-500 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded"
        onClick={async () => {
          setUrl(null);
          setLoading(true);
          const blobUrl = await compose({ openingUrl, closingUrl, filename });
          setUrl(blobUrl);
          setLoading(false);
        }}
      >
        {loading ? "🐰 Composing..." : "🎩 Compose"}
      </button>
      {url && <audio src={url} className="w-full" controls />}
    </React.Fragment>
  );
};

const RecordTab = ({ episode, setEpisode, uid }) => {
  const {
    date,
    prompt,
    promptSource,
    sponsor,
    sponsorDescription,
    answer,
  } = episode;

  const [closingUrl, setClosingUrl] = useState(null);
  const [openingUrl, setOpeningUrl] = useState(null);

  return (
    <div>
      <p className="text-gray-700 text-base mb-2">
        Welcome to Your Thoughts—the show featuring your own thoughts, coming
        from inside your brain. I'm your host Christian Genco.
      </p>

      <p className="text-gray-700 text-base mb-2">
        Today's prompt {promptSource ? `comes from ${promptSource}` : "is"}:{" "}
        <strong>{prompt}</strong>. Enjoy the next two minutes of Your Thoughts.
      </p>

      <RecordButton
        filePath={`users/${uid}/episodes/${episode.id}/opening.webm`}
        onUrlChange={url => setOpeningUrl(url)}
      />

      <hr className="my-4" />

      <p className="text-gray-700 text-base mb-2">
        That concludes today's episode of Your Thoughts. Today's episode is
        sponsored by <strong>{sponsor}</strong>.
      </p>
      <p className="text-gray-700 text-base mb-2">
        To share what you thought on today's episode or to send in a suggested
        prompt, visit our website at yourthoughts.fm. Please subscribe to this
        show on Apple Podcasts, Spotify, Overcast, or wherever you listen. Thank
        you for joining us today on Your Thoughts.
      </p>

      <RecordButton
        filePath={`users/${uid}/episodes/${episode.id}/closing.webm`}
        onUrlChange={url => setClosingUrl(url)}
      />

      <hr className="my-4" />

      <ComposeButton
        filename={date}
        openingUrl={openingUrl}
        closingUrl={closingUrl}
      />
    </div>
  );
};

export default RecordTab;
